/**
 * The legal-transition graph for the default pilot workflow state machine.
 *
 * This module lives in the backend domain layer under `src/server/`, kept out of
 * the React `src/components` tree, and mirrors the read-only decision style of
 * `./authorization`: it is a pure, side-effect-free source of truth for *which*
 * workflow transitions are legal, independent of *who* may make them (that
 * authorization decision lives in `./authorization`) and of any persisted state.
 *
 * The graph encodes the default pilot workflow:
 *
 * - the linear progression
 *   `Draft → Ready for Development → In Development → Awaiting Internal Review → Internal Review →
 *   Awaiting QA Approval → Approved → Final Descriptor Signed Off →
 *   Exported and Locked`;
 * - `In Development → Awaiting QA Approval` when no internal reviewer is assigned;
 * - the `Internal Review → Changes Required → In Development` rework loop;
 * - a Client Admin's return to `Draft` from any state before the export locks; and
 * - an `Archived` terminal reachable from the working states.
 *
 * @module server/workflow
 */

import type { WorkflowState } from "./types";

/**
 * The legal transitions of the default pilot workflow, keyed by the state a
 * programme is transitioning *from*. Each value lists the states a programme may
 * legally move *to* from that state; every one of the eleven {@link WorkflowState}
 * values has an entry (terminal states map to an empty list).
 */
export const ALLOWED_TRANSITIONS: Record<WorkflowState, readonly WorkflowState[]> = {
  Draft: ["Ready for Development", "Archived"],
  "Ready for Development": ["Draft", "In Development", "Archived"],
  // Straight to QA approval when nobody is assigned to review internally.
  "In Development": ["Awaiting Internal Review", "Awaiting QA Approval", "Draft", "Archived"],
  "Awaiting Internal Review": ["Internal Review", "Draft", "Archived"],
  "Internal Review": ["Awaiting QA Approval", "Changes Required", "Draft", "Archived"],
  "Changes Required": ["In Development", "Draft", "Archived"],
  "Awaiting QA Approval": ["Approved", "Changes Required", "Draft", "Archived"],
  Approved: ["Final Descriptor Signed Off", "Draft", "Archived"],
  "Final Descriptor Signed Off": ["Exported and Locked", "Draft", "Archived"],
  "Exported and Locked": [],
  Archived: [],
};

/**
 * Whether moving from one workflow state to another is a legal transition in the
 * default pilot workflow.
 *
 * The decision is read only from {@link ALLOWED_TRANSITIONS}: `to` must be listed
 * under `from`. Skipping a required state (e.g. `Draft → Approved`) is denied.
 *
 * @param from - The workflow state the programme is transitioning from.
 * @param to - The workflow state the programme is transitioning to.
 * @returns `true` when `to` is a legal transition from `from`, otherwise `false`.
 */
export function isTransitionAllowed(from: WorkflowState, to: WorkflowState): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

/** Whether a programme in this state is open for module and content editing. */
export function isWorkflowEditable(workflowState: WorkflowState): boolean {
  return (
    workflowState === "Draft" ||
    workflowState === "Ready for Development" ||
    workflowState === "In Development" ||
    workflowState === "Changes Required"
  );
}
